import { useCallback, useEffect, useState } from "react";
import { ArrowUpCircle } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { isNativeApp } from "@/lib/native/platform";
import {
  checkForAppUpdate,
  isUpdateSnoozed,
  openStoreListing,
  snoozeUpdate,
  type UpdateCheckResult,
} from "@/lib/native/appUpdate";

/** Wait a moment after launch so the check never competes with first paint. */
const INITIAL_DELAY_MS = 4000;
/** Don't re-hit the endpoint more than once every 30 minutes. */
const MIN_INTERVAL_MS = 30 * 60 * 1000;

/**
 * In-app update prompt (native shell only).
 *
 * Checks the published store build on launch and whenever the app returns to
 * the foreground. A newer build shows a dismissable "Update available" dialog;
 * a build below `min_supported_build` shows a blocking prompt that can only be
 * resolved by updating. Renders nothing on the web.
 */
export const NativeUpdatePrompt = () => {
  const [result, setResult] = useState<UpdateCheckResult | null>(null);
  const [open, setOpen] = useState(false);

  const runCheck = useCallback(async () => {
    const res = await checkForAppUpdate();
    if (res.status !== "optional" && res.status !== "required") return;
    if (!res.release) return;
    if (res.status === "optional" && isUpdateSnoozed(res.release.build)) return;
    setResult(res);
    setOpen(true);
  }, []);

  useEffect(() => {
    if (!isNativeApp()) return;

    let cancelled = false;
    let lastCheck = 0;

    const maybeCheck = () => {
      if (cancelled) return;
      if (Date.now() - lastCheck < MIN_INTERVAL_MS) return;
      lastCheck = Date.now();
      void runCheck();
    };

    const timer = window.setTimeout(maybeCheck, INITIAL_DELAY_MS);

    let removeListener: (() => void) | undefined;
    void (async () => {
      try {
        const { App } = await import("@capacitor/app");
        const handle = await App.addListener("appStateChange", ({ isActive }) => {
          if (isActive) maybeCheck();
        });
        if (cancelled) void handle.remove();
        else removeListener = () => void handle.remove();
      } catch {
        /* plugin unavailable — launch check is enough */
      }
    })();

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      removeListener?.();
    };
  }, [runCheck]);

  if (!result?.release) return null;

  const required = result.status === "required";
  const { release } = result;

  const handleUpdate = () => {
    void openStoreListing(release);
    // Keep a required prompt on screen: the user must come back updated.
    if (!required) setOpen(false);
  };

  const handleLater = () => {
    snoozeUpdate(release.build);
    setOpen(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={(next) => (required ? undefined : setOpen(next))}>
      <AlertDialogContent
        className="max-w-sm"
        onEscapeKeyDown={(e) => required && e.preventDefault()}
      >
        <AlertDialogHeader>
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <ArrowUpCircle className="h-6 w-6 text-primary" aria-hidden="true" />
          </div>
          <AlertDialogTitle className="text-center">
            {required ? "Update required" : "Update available"}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-center">
            {required
              ? `This version of HIBANE Desk is no longer supported. Update to ${release.version} to keep using the app.`
              : `HIBANE Desk ${release.version} is available. You're on ${result.currentVersion ?? "an older version"}.`}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {release.release_notes ? (
          <p className="max-h-40 overflow-y-auto whitespace-pre-line rounded-md bg-muted/50 p-3 text-sm text-muted-foreground">
            {release.release_notes}
          </p>
        ) : null}

        <AlertDialogFooter className="flex-col gap-2 sm:flex-col">
          <AlertDialogAction onClick={handleUpdate} className="w-full">
            {required ? "Update now" : "Update"}
          </AlertDialogAction>
          {!required && (
            <AlertDialogCancel onClick={handleLater} className="mt-0 w-full">
              Later
            </AlertDialogCancel>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default NativeUpdatePrompt;
